import React from 'react';

import { Grid, RHFSelect, RHFTextInput } from '@cowbell/ui';

import { OrgDomainField } from './OrgDomainField';
import { OrgFieldHint } from './OrgFieldHint';
import { OrgIndustryField } from './OrgIndustryField';
import { useFetchOwnershipTypes } from '../../../../console/agencies/quotes/PrimeX/AccountDetails/forms/useFetchOwnershipTypes';
import { useCowbellTranslations } from '../../../../i18n/translations';
import { FIELD_SPAN, QUARTER_SPAN } from './org-form-layout';
import { OrgCompletionBadge } from './OrgCompletionBadge';
import { OrgFormSection } from './OrgFormSection';
import { OrgPanel } from './OrgPanel';
import { OrgSubsidiaryRow, type FranchiseValue } from './OrgSubsidiaryRow';
import type { OrgDetailsFormApi } from './useOrgDetailsForm';

const DUNS_TOOLTIP =
  "Nine-digit Dun & Bradstreet identifier. Leave blank if you don't have one.";

interface OrgDetailsPanelProps {
  readonly form: OrgDetailsFormApi;
  readonly canVerify: boolean;
}

/**
 * The Organization Details panel: identity, then size and ownership, then
 * industry and web presence, then the subsidiary question -- the groups the
 * supplied design uses.
 *
 * <p>Every field is editable. The accordion this replaces rendered read-only
 * text in place of the ownership and industry pickers whenever
 * `submissions_view` was on (FIR-21111); there is no such branch here.
 */
export const OrgDetailsPanel = ({ form, canVerify }: OrgDetailsPanelProps) => {
  const { t } = useCowbellTranslations();
  const { ownershipTypeOptions } = useFetchOwnershipTypes();
  const { setValue, watch } = form.methods;
  // Watched, not read once: a one-shot getValues() leaves the pill showing the
  // old answer until something else re-renders the panel.
  const isFranchise = watch('isFranchise');

  const status = (
    <>
      <OrgCompletionBadge missing={form.missingCount} />
      {canVerify ? (
        <img src="images/VerifiedTabYes.svg" alt="Verified" height={18} />
      ) : null}
    </>
  );

  const onFranchiseChange = (value: boolean) => {
    // shouldDirty, or answering this question alone never raises the save bar.
    setValue(
      'isFranchise',
      { value, label: value ? 'Yes' : 'No' },
      { shouldDirty: true }
    );
  };

  return (
    <OrgPanel
      id="details"
      title={`${t('organization')} details`}
      description="Identity, size, and classification used across your policies."
      status={status}
    >
      <Grid gutter="md">
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput name="name" label="Legal name" withAsterisk />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name="dbaOrTradestyle"
            label="Doing business as"
            placeholder="Trade name, if different"
          />
        </Grid.Col>

        <OrgFormSection title="Size & ownership" />

        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name="revenue"
            label={`Projected ${t('revenue').toLowerCase()}`}
            withAsterisk
            inputMode="numeric"
            leftSection="$"
          />
        </Grid.Col>
        <Grid.Col span={QUARTER_SPAN}>
          <RHFTextInput
            name="noOfEmployeesAll"
            label="Employees"
            withAsterisk
            inputMode="numeric"
          />
        </Grid.Col>
        <Grid.Col span={QUARTER_SPAN}>
          <RHFTextInput
            name="yearEstablished"
            label="Year established"
            withAsterisk
            inputMode="numeric"
          />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <RHFSelect
            name="ownershipType"
            label="Ownership type"
            withAsterisk
            data={ownershipTypeOptions}
            placeholder={t('account:ownershipType.placeholder')}
          />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput
            name="dunsNumber"
            label={<OrgFieldHint label="DUNS number" hint={DUNS_TOOLTIP} />}
            placeholder="000000000"
          />
        </Grid.Col>

        <OrgFormSection title="Industry & web presence" />

        <Grid.Col span={FIELD_SPAN}>
          <OrgIndustryField
            label="Industry code"
            defaultValue={form.defaultIndustryCode}
            language={form.industryLanguage}
            onSelect={form.onIndustrySelect}
          />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <RHFTextInput name="secondaryIndustry" label="Secondary industry" />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <OrgDomainField
            name="domainName"
            label="Primary web domain"
            placeholder="domain.com"
          />
        </Grid.Col>
        <Grid.Col span={FIELD_SPAN}>
          <OrgDomainField
            name="domains"
            label="Secondary web domains"
            placeholder="domain.com, domain.net"
            isMulti
          />
        </Grid.Col>

        <OrgSubsidiaryRow
          legend={`Is your ${t(
            'organization'
          ).toLowerCase()} a subsidiary or a franchise?`}
          value={isFranchise as FranchiseValue}
          onChange={onFranchiseChange}
        />
      </Grid>
    </OrgPanel>
  );
};

export default OrgDetailsPanel;
